import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { compose } from 'redux';
import {
  CTA,
  CustomInput,
  CustomSelect,
  NoResult,
  Text,
} from '../../../../ui/atoms';
import {
  FiltersContainer,
  Filter,
  SelectsContainer,
  CoursesContainer,
  CourseItem,
  ResultsSorting,
} from '../style';
import routes from '../../../../routes';
import CardCourses from '../../../../ui/atoms/Cards/CardCourses';
import { flattenCategories } from '../../../../utils/categories';
import {
  COURSE_LIST_GET,
  COURSE_LIST_RESET,
} from '../../../redux/actions/courselist';
import Pagination from '../../../../ui/atoms/Pagination';
import SortBy from '../../../../ui/atoms/SortBy';
import { SEARCH_COURSES_LIST_GET } from '../../../redux/actions/search';
import {
  DURATION_OPTIONS,
  formatFilterOpt,
  getFilterLabel,
} from '../../../../utils/labels';
import { withMediaQueries } from '../../../hoc/withMediaQueries';
import { StickyFiltersWrapper } from '../../../../ui/atoms/CTA/style';
import { MODAL_OPEN } from '../../../redux/actions';
import ModalFilters, {
  ModalFiltersId,
} from '../../../../ui/atoms/Modals/ModalFilters';

const CourseResults = ({
  mediaIsPhone,
  currentCatalog,
  currentArea,
  active = true,
  list,
  filters,
  pager,
  getCourseList,
  courseListReset,
  searchCourses,
  modalOpen,
}) => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  // filters
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState({});
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue(searchParams.get('course') || '');

    return () => {
      courseListReset();
      setSelectedFilters({});
      setSubjects([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetCourses = (page) => {
    if (currentCatalog?.id) {
      const parsedFilters = {
        ...selectedFilters,
        typology: 'course',
        catalog_id: currentCatalog?.id,
        categories: selectedFilters?.subject || currentArea?.id,
        categoryType: selectedFilters?.subject
          ? 'single_categories'
          : 'parent_categories',
        is_module: false,
        page,
      };
      const title = parsedFilters?.title;
      delete parsedFilters?.title;
      delete parsedFilters?.subject;
      if (title) {
        searchCourses({ title, filters: parsedFilters });
      } else getCourseList(parsedFilters);
    }
  };

  useEffect(() => {
    if (currentArea?.id) {
      const newSubjects = flattenCategories(currentArea?.children, 0, true);
      setSubjects(newSubjects);
    } else setSubjects([]);
    setSelectedFilters({
      ...selectedFilters,
      subject: undefined,
      title: searchParams.get('course'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentArea]);

  useEffect(() => {
    if (Object.keys(selectedFilters)?.length > 0) handleGetCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, currentCatalog]);

  const onClickSearch = () => {
    // update url params to preserve search on back
    const newParams = new URLSearchParams(searchParams);
    newParams.set('course', searchValue);
    setSearchParams(newParams);
    setSelectedFilters({ ...selectedFilters, title: searchValue });
  };

  const handleOnChange = ({ key, value }) => {
    const newFilters = { ...selectedFilters };
    newFilters[key] = value?.value;
    setSelectedFilters(newFilters);
  };

  const loadMore = (page) => handleGetCourses(page);
  const setItemsPerPage = (v) =>
    handleOnChange({ key: 'items_per_page', value: v });

  return (
    <>
      {mediaIsPhone ? (
        <StickyFiltersWrapper mb={16} stickyPosition={1}>
          <CustomInput
            placeholder="Cosa vuoi cercare?..."
            isSearch
            value={searchValue}
            icon="search-sm"
            isClearable
            onChange={(v) => setSearchValue(v)}
            onClickIcon={onClickSearch}
            onKeyPress={(event) => {
              if (event.key === 'Enter') onClickSearch();
            }}
          />
          <CTA
            classes="small-btn"
            btnText="Filtri"
            type="secondary"
            fullsize
            onClick={() =>
              modalOpen(ModalFiltersId, {
                filters: {
                  subject: subjects,
                  ...filters,
                  duration: DURATION_OPTIONS,
                },
                activeFilters: selectedFilters,
              })
            }
          />
        </StickyFiltersWrapper>
      ) : (
        <>
          <FiltersContainer>
            <SelectsContainer>
              <Filter>
                <CustomInput
                  placeholder="Cosa vuoi cercare?..."
                  isSearch
                  value={searchValue}
                  icon="search-sm"
                  isClearable
                  onChange={(v) => setSearchValue(v)}
                  onClickIcon={onClickSearch}
                  onKeyPress={(event) => {
                    if (event.key === 'Enter') onClickSearch();
                  }}
                />
              </Filter>
              {subjects?.length > 0 && (
                <Filter>
                  <CustomSelect
                    placeholder="Materia"
                    options={subjects}
                    isClearable
                    onChange={(v) =>
                      setSelectedFilters({
                        ...selectedFilters,
                        subject: v?.value,
                      })
                    }
                    selected={
                      subjects?.find(
                        (s) => s.value === selectedFilters?.subject
                      ) ?? null
                    }
                  />
                </Filter>
              )}
              {(Object.keys(filters) || []).map((key, i) => (
                <Filter key={`filter-${i}`}>
                  <CustomSelect
                    placeholder={getFilterLabel(key)}
                    options={filters[key].map((opt) => formatFilterOpt(opt))}
                    onChange={(value) => handleOnChange({ key, value })}
                    isClearable
                  />
                </Filter>
              ))}
              <Filter>
                <CustomSelect
                  placeholder="Durata"
                  options={DURATION_OPTIONS}
                  isClearable
                  onChange={(v) =>
                    setSelectedFilters({
                      ...selectedFilters,
                      duration: v?.value,
                    })
                  }
                />
              </Filter>
            </SelectsContainer>
          </FiltersContainer>
          <ResultsSorting>
            <Text type="b3">{pager?.total_items || 0} corsi disponibili</Text>
            <SortBy
              onChange={(v) => {
                setSelectedFilters({
                  ...selectedFilters,
                  sort_by: v?.value,
                });
              }}
            />
          </ResultsSorting>
        </>
      )}
      <CoursesContainer>
        {[...list].map((item) => {
          if (!item.id) return null;
          return (
            <CourseItem key={`course-${item.id}`}>
              <CardCourses
                item={item}
                onClick={() =>
                  navigate(`${routes.courseDetails.path}/${item.id}/panoramica`)
                }
              />
            </CourseItem>
          );
        })}
      </CoursesContainer>
      {list.length <= 0 && <NoResult />}
      <Pagination
        pager={pager}
        callBack={loadMore}
        setItemsPerPage={setItemsPerPage}
      />
      {mediaIsPhone && active && (
        <ModalFilters
          onClick={(newFilters) =>
            setSelectedFilters({
              ...newFilters,
              title: selectedFilters.title,
            })
          }
        />
      )}
    </>
  );
};

CourseResults.propTypes = {
  mediaIsPhone: PropTypes.bool,
  currentCatalog: PropTypes.object,
  currentArea: PropTypes.object,
  active: PropTypes.bool,
  // HOC (connect, state)
  list: PropTypes.array.isRequired,
  filters: PropTypes.object,
  pager: PropTypes.object.isRequired,
  // HOC (connect, dispatch)
  getCourseList: PropTypes.func.isRequired,
  courseListReset: PropTypes.func.isRequired,
  searchCourses: PropTypes.func.isRequired,
  modalOpen: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => {
      const { list = [], filters, pager = {} } = state.courselist.courses;
      return {
        list,
        filters,
        pager,
      };
    },
    (dispatch) => ({
      getCourseList: (filters) =>
        dispatch({
          type: COURSE_LIST_GET._REQUEST,
          filters,
        }),
      courseListReset: () => dispatch({ type: COURSE_LIST_RESET }),
      searchCourses: ({ title, filters }) =>
        dispatch({
          type: SEARCH_COURSES_LIST_GET._REQUEST,
          filters,
          title,
          catalogPage: true,
        }),
      modalOpen: (id, payload) => dispatch({ type: MODAL_OPEN, id, payload }),
    })
  ),
  withMediaQueries
)(CourseResults);
